import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { Controller, useFormContext } from "react-hook-form";
import clsx from "clsx";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import type { OrderFormValues } from "../../../../types/forms";
import { stateOptions } from "../../../../constants";

export default function ReturnAddress() {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<OrderFormValues>();

  return (
    <div className="mx-auto mt-6 px-3 py-7 md:px-6 max-w-7xl">
      <div className="flex flex-col gap-2 mb-3">
        <h2 className="text-lg font-medium text-gray-950">Return Address</h2>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label
            htmlFor="recipientName"
            className="block text-sm/6 font-normal text-gray-950"
          >
            Full Name
          </label>
          <div>
            <input
              id="recipientName"
              type="text"
              autoComplete="off"
              placeholder="Enter your full name"
              {...register("addressInfo.shippingInfo.recipientName", {
                required: "Full name is required",
                minLength: {
                  value: 1,
                  message: "Full name must be at least 1 character",
                },
                maxLength: {
                  value: 100,
                  message: "Full name must be at most 100 characters",
                },
                setValueAs: (v) => v?.trim(),
              })}
              className={clsx(
                "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
                "focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500",
                errors.addressInfo?.shippingInfo?.recipientName
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-200",
              )}
            />
            {errors.addressInfo?.shippingInfo?.recipientName && (
              <p className="mt-2 text-sm text-red-500">
                {errors.addressInfo?.shippingInfo?.recipientName.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="street"
            className="block text-sm/6 font-normal text-gray-950"
          >
            Address
          </label>
          <div>
            <input
              id="street"
              type="text"
              autoComplete="off"
              placeholder="Enter your address"
              {...register("addressInfo.shippingInfo.address.street", {
                required: "Address is required",
                minLength: {
                  value: 1,
                  message: "Street must be at least 1 character",
                },
                maxLength: {
                  value: 100,
                  message: "Street must be at most 100 characters",
                },
                setValueAs: (v) => v?.trim(),
              })}
              className={clsx(
                "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
                "focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500",
                errors.addressInfo?.shippingInfo?.address?.street
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-200",
              )}
            />
            {errors.addressInfo?.shippingInfo?.address?.street && (
              <p className="mt-2 text-sm text-red-500">
                {errors.addressInfo?.shippingInfo?.address?.street.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="unit"
            className="block text-sm/6 font-normal text-gray-950"
          >
            Apt, Suite, Building (optional)
          </label>
          <div>
            <input
              id="unit"
              type="text"
              autoComplete="off"
              placeholder="Enter Apt, Suite, Building"
              {...register("addressInfo.shippingInfo.address.unit", {
                maxLength: {
                  value: 50,
                  message: "Unit must be at most 50 characters",
                },
                setValueAs: (v) => v?.trim(),
              })}
              className={clsx(
                "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
                "focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500",
                errors.addressInfo?.shippingInfo?.address?.unit
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-200",
              )}
            />
            {errors.addressInfo?.shippingInfo?.address?.unit && (
              <p className="mt-2 text-sm text-red-500">
                {errors.addressInfo?.shippingInfo?.address?.unit.message}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="city"
              className="block text-sm/6 font-normal text-gray-950"
            >
              City
            </label>
            <div>
              <input
                id="city"
                type="text"
                autoComplete="off"
                placeholder="Enter your City"
                {...register("addressInfo.shippingInfo.address.city", {
                  required: "City is required",
                  minLength: {
                    value: 1,
                    message: "City must be at least 1 character",
                  },
                  maxLength: {
                    value: 50,
                    message: "City must be at most 50 characters",
                  },
                  setValueAs: (v) => v?.trim(),
                })}
                className={clsx(
                  "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
                  "focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500",
                  errors.addressInfo?.shippingInfo?.address?.city
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200",
                )}
              />
              {errors.addressInfo?.shippingInfo?.address?.city && (
                <p className="mt-2 text-sm text-red-500">
                  {errors.addressInfo?.shippingInfo?.address?.city.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Controller
              name="addressInfo.shippingInfo.address.state"
              control={control}
              rules={{ required: "State is required" }}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-2">
                  <label className="block text-sm/6 font-normal text-gray-950">
                    State
                  </label>

                  <Listbox
                    value={
                      stateOptions.find((o) => o.value === field.value) ?? null
                    }
                    onChange={(opt) => field.onChange(opt?.value)}
                  >
                    <ListboxButton
                      className={clsx(
                        "flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-sm outline-none",
                        "focus:ring-2 focus:ring-blue-500",
                        fieldState.error
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200",
                      )}
                    >
                      <span className="truncate">
                        {stateOptions.find((o) => o.value === field.value)
                          ?.label ?? (
                          <span className="text-gray-500">Select state</span>
                        )}
                      </span>

                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    </ListboxButton>

                    <ListboxOptions
                      anchor="bottom"
                      className="mt-1 w-(--button-width) h-46 overflow-auto  rounded-xl border border-gray-200 bg-white p-1 shadow outline-none"
                    >
                      {stateOptions.map((opt) => (
                        <ListboxOption
                          key={opt.value}
                          value={opt}
                          className="cursor-pointer rounded-lg px-3 py-2 text-sm data-focus:bg-blue-50 data-focus:text-blue-500"
                        >
                          {opt.label}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>

                  {fieldState.error && (
                    <p className="text-sm text-red-600">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="zip"
              className="block text-sm/6 font-normal text-gray-950"
            >
              ZIP Code
            </label>
            <div>
              <input
                id="zip"
                type="text"
                autoComplete="off"
                placeholder="Enter your ZIP Code"
                {...register("addressInfo.shippingInfo.address.zip", {
                  required: "ZIP Code is required",
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "ZIP must be a valid US postal code",
                  },
                  setValueAs: (v) => v?.trim(),
                })}
                className={clsx(
                  "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
                  "focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500",
                  errors.addressInfo?.shippingInfo?.address?.zip
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200",
                )}
              />
              {errors.addressInfo?.shippingInfo?.address?.zip && (
                <p className="mt-2 text-sm text-red-500">
                  {errors.addressInfo?.shippingInfo?.address?.zip.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
